import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, doc, getDoc, query, where, onSnapshot, orderBy, getDocs } from "firebase/firestore";
import { Button, DatePicker, Dropdown, Input, Select } from 'antd';
import { ChevronRight, Download, MoreVertical, Package, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import StatusBadge from './components/StatusBadge.js';
import { isSalesUser } from './accessControl.js';
import { downloadDeliveredReportCsv } from './utils/deliveredReportCsv.js';

const { RangePicker } = DatePicker;

const normalizeOptionList = (values) => Array.from(new Set(values.filter(Boolean)))
  .sort((a, b) => String(a).localeCompare(String(b)))
  .map((value) => ({ value, label: value }));

const getDeliveredDate = (delivery) => {
  const value = delivery?.deliveredAt?.toDate
    ? delivery.deliveredAt.toDate()
    : (delivery?.deliveryDetails?.deliveryDate || delivery?.updatedAt?.toDate?.() || delivery?.updatedAt);
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const DeliveredList = ({ db, user }) => {
  const navigate = useNavigate();
  const isSales = isSalesUser(user);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [filterWarehouses, setFilterWarehouses] = useState([]);
  const [filterTypes, setFilterTypes] = useState([]);
  const [filterFirms, setFilterFirms] = useState([]);
  const [filterTransporters, setFilterTransporters] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const orderCacheRef = useRef(new Map());

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    // Fetch dispatches that are Delivered
    const q = query(
      collection(db, "dispatches"), 
      where("dispatchStatus", "==", "delivered"),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const deliveryList = querySnapshot.docs.map(doc => ({
        id: doc.id, 
        ...doc.data() 
      }));
      setDeliveries(isSales ? deliveryList.filter((delivery) => delivery.ownerUid === user?.uid) : deliveryList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching Delivered list: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db, isSales, user?.uid]);

  const warehouseOptions = useMemo(() => normalizeOptionList(deliveries.map((delivery) => delivery.warehouse)), [deliveries]);
  const typeOptions = useMemo(() => normalizeOptionList(deliveries.map((delivery) => delivery.typeOfMOC)), [deliveries]);
  const firmOptions = useMemo(() => normalizeOptionList(deliveries.map((delivery) => delivery.firmId)), [deliveries]);
  const transporterOptions = useMemo(() => normalizeOptionList(deliveries.map((delivery) => delivery.dispatchedThrough)), [deliveries]);

  const hasActiveFilters = Boolean(
    searchTerm.trim() ||
    filterDateRange?.length ||
    filterTypes.length ||
    filterWarehouses.length ||
    filterFirms.length ||
    filterTransporters.length
  );

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterDateRange(null);
    setFilterTypes([]);
    setFilterWarehouses([]);
    setFilterFirms([]);
    setFilterTransporters([]);
  };

  const filteredDeliveries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const startDate = filterDateRange?.[0]?.startOf('day')?.toDate?.() || null;
    const endDate = filterDateRange?.[1]?.endOf('day')?.toDate?.() || null;

    return deliveries.filter(d => {
      const matchesSearch =
        !normalizedSearch ||
        d.customerName?.toLowerCase().includes(normalizedSearch) ||
        d.mocNo?.toLowerCase().includes(normalizedSearch) ||
        d.challanNo?.toLowerCase().includes(normalizedSearch) ||
        d.vehicleNumber?.toLowerCase().includes(normalizedSearch);
      const deliveredDate = getDeliveredDate(d);
      const deliveredDateOnly = deliveredDate ? new Date(deliveredDate) : null;
      if (deliveredDateOnly) deliveredDateOnly.setHours(0, 0, 0, 0);

      const matchesStartDate = !startDate || (deliveredDateOnly && deliveredDateOnly >= startDate);
      const matchesEndDate = !endDate || (deliveredDateOnly && deliveredDateOnly <= endDate);
      const matchesWarehouse = filterWarehouses.length === 0 || filterWarehouses.includes(d.warehouse);
      const matchesType = filterTypes.length === 0 || filterTypes.includes(d.typeOfMOC);
      const matchesFirm = filterFirms.length === 0 || filterFirms.includes(d.firmId);
      const matchesTransporter = filterTransporters.length === 0 || filterTransporters.includes(d.dispatchedThrough);
      return matchesSearch && matchesStartDate && matchesEndDate && matchesWarehouse && matchesType && matchesFirm && matchesTransporter;
    });
  }, [deliveries, searchTerm, filterDateRange, filterWarehouses, filterTypes, filterFirms, filterTransporters]);

  const getLinkedOrdersForExport = async (rows) => {
    const uniqueOrderIds = Array.from(new Set(rows.map((row) => row.orderId).filter(Boolean)));
    const missingOrderIds = uniqueOrderIds.filter((orderId) => !orderCacheRef.current.has(orderId));

    await Promise.all(missingOrderIds.map(async (orderId) => {
      const orderSnap = await getDoc(doc(db, 'orders', orderId));
      if (!orderSnap.exists()) {
        orderCacheRef.current.set(orderId, null);
        return;
      }

      const orderData = { id: orderSnap.id, ...orderSnap.data() };

      // If order doesn't have totalMeter or totalMeterNumber, try fetching dispatches under the order
      if (orderData.totalMeter === undefined && orderData.totalMeterNumber === undefined) {
        try {
          const dispatchesCol = collection(db, 'orders', orderId, 'dispatches');
          const dispatchesSnap = await getDocs(dispatchesCol);
          for (const d of dispatchesSnap.docs) {
            const dData = d.data();
            if (dData.totalMeter !== undefined) {
              orderData.totalMeter = dData.totalMeter;
              break;
            }
            if (dData.totalMeterNumber !== undefined) {
              orderData.totalMeterNumber = dData.totalMeterNumber;
              break;
            }
          }
        } catch (err) {
          console.warn('Could not read order dispatches for totalMeter fallback', orderId, err);
        }
      }

      orderCacheRef.current.set(orderId, orderData);
    }));

    return orderCacheRef.current;
  };

  const handleDownloadCsv = async () => {
    if (filteredDeliveries.length === 0) {
      alert('No delivered records match the current filters.');
      return;
    }

    setIsExporting(true);
    try {
      const ordersById = await getLinkedOrdersForExport(filteredDeliveries);
      downloadDeliveredReportCsv({
        deliveries: filteredDeliveries,
        ordersById
      });
    } catch (error) {
      console.error('Error exporting delivered report:', error);
      alert('Could not export the delivered report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportMenuItems = [
    {
      key: 'download-csv',
      icon: <Download size={14} />,
      label: isExporting ? 'Preparing CSV...' : 'Download CSV',
      disabled: isExporting || filteredDeliveries.length === 0
    }
  ];

  const formatDateTime = (value) => {
      if (!value) return '---';
      const date = value?.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return '---';
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Delivered Orders</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Log of all successfully delivered materials and PODs.</p>
        </div>
      </div>

      <div style={filterSectionStyle}>
        <div style={{ ...filterInputGroupStyle, minWidth: '320px' }}>
          <label style={filterLabelStyle}>Customer Search</label>
          <Input.Search
            allowClear
            placeholder="Search by Customer, MOC, DC, or Vehicle No..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: '260px' }}>
          <label style={filterLabelStyle}>Delivered Date</label>
          <RangePicker
            value={filterDateRange}
            onChange={setFilterDateRange}
            style={{ width: '100%' }}
          />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>MOC Type</label>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="All Types"
            options={typeOptions}
            value={filterTypes}
            onChange={setFilterTypes}
            style={{ width: '100%' }}
          />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Warehouse</label>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="All Warehouses"
            options={warehouseOptions}
            value={filterWarehouses}
            onChange={setFilterWarehouses}
            style={{ width: '100%' }}
          />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Firm</label>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="All Firms"
            options={firmOptions}
            value={filterFirms}
            onChange={setFilterFirms}
            style={{ width: '100%' }}
          />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Transporter</label>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="All Transporters"
            options={transporterOptions}
            value={filterTransporters}
            onChange={setFilterTransporters}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ ...filterInputGroupStyle, flex: '0 0 auto', justifyContent: 'flex-end' }}>
          <div style={filterActionsStyle}>
            <Button onClick={handleClearFilters} disabled={!hasActiveFilters}>
              Clear
            </Button>
            <Dropdown
              menu={{
                items: exportMenuItems,
                onClick: ({ key }) => {
                  if (key === 'download-csv') handleDownloadCsv();
                }
              }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                aria-label="Report actions"
                icon={<MoreVertical size={16} />}
                loading={isExporting}
              />
            </Dropdown>
          </div>
        </div>
      </div>

      {deliveries.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Package size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No deliveries yet.</h3>
          <p style={{ color: '#64748b' }}>Completed deliveries will appear here.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Delivery Date</th>
                <th style={{...thStyle, textAlign:'left'}}>Customer / Destination</th>
                <th style={thStyle}>Salesperson</th>
                <th style={thStyle}>DC No.</th>
                <th style={thStyle}>Receiver Info</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeliveries.map((d) => (
                <tr 
                  key={d.id} 
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => navigate(`/ops/delivered/${d.id}`)}
                  className="hover-highlight"
                >
                  <td style={tdStyle}>
                    <div style={{fontWeight: 'bold'}}>
                      {formatDateTime(d.deliveredAt?.toDate ? d.deliveredAt.toDate() : (d.deliveryDetails?.deliveryDate || d.updatedAt?.toDate?.() || d.updatedAt))}
                    </div>
                  </td>
                  <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{d.customerName}</div>
                    <div style={{fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px'}}>
                        <MapPin size={12} /> {d.shipToAddress?.substring(0, 40) || d.shippingAddress?.substring(0, 40)}...
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '600' }}>
                      {d.salesperson || d.salesPerson || d.ownerName || '---'}
                    </div>
                  </td>
                  <td style={tdStyle}><strong>{d.challanNo}</strong></td>
                  <td style={tdStyle}>
                      <div style={{fontSize: '13px'}}>{d.deliveryDetails?.receiverName || '---'}</div>
                      <div style={{fontSize: '11px', color: '#64748b'}}>{d.deliveryDetails?.receiverContact || ''}</div>
                  </td>
                  <td style={tdStyle}>
                    <StatusBadge status="delivered" iconSize={12} />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#2563eb', fontWeight: '600' }}>
                      View <ChevronRight size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };
const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '190px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterActionsStyle = { display: 'flex', alignItems: 'center', gap: '8px' };

export default DeliveredList;
